// The monthly activity imports, admin only: two upload sections, one per
// source, and every past import. The review itself lives on the import's
// own page.
//
// Franchisee invoices are what the clinics billed FTFS: owner revenue, the
// figure every role sees. Customer bills are what FTFS billed the customer:
// FTFS revenue, admin only. Keeping them as two sections keeps the code and
// the question "which pile is this" simple.

import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { listActivityImports, createActivityImport, removeActivityImport } from '../api.js'
import { useApiForm } from '../useApiForm.js'
import { useConfirm } from '../confirm.jsx'
import { Button, ButtonLink, EmptyState, Notice, Panel, Pill } from '../components/ui/index.js'
import { importStatusTone, activitySourceTone } from '../tones/activity.js'
import {
  importStatusLabel,
  activitySourceLabel,
  ACTIVITY_SOURCES,
  monthLabel,
  currentMonth,
  shiftMonth,
  formatInstant,
  SEPARATOR,
  formatBytes,
} from '../vocab.js'

// One upload section: pick the month, attach the documents, start the
// import. The source is fixed by the section it sits in.
function ImportSection({ source, title, description }) {
  const navigate = useNavigate()
  const fileInput = useRef(null)
  const [month, setMonth] = useState(currentMonth())
  const [files, setFiles] = useState([])
  const { saving, error, submit } = useApiForm()

  function addFiles(event) {
    const picked = [...(event.target.files ?? [])]
    setFiles((current) => [...current, ...picked].slice(0, 20))
    event.target.value = ''
  }

  function dropFile(index) {
    setFiles((current) => current.filter((_, i) => i !== index))
  }

  async function start() {
    const result = await submit(() => createActivityImport(month, files, source))
    if (result) navigate(`/activity/imports/${result.import.id}`)
  }

  return (
    <Panel title={title} support={description}>
      {error ? <Notice tone="bad">{error}</Notice> : null}

      <div className="toolbar">
        <Button
          variant="icon"
          aria-label={`Previous month for ${title}`}
          onClick={() => setMonth(shiftMonth(month, -1))}
        >
          <ChevronLeft size={16} strokeWidth={1.75} />
        </Button>
        <span className="month-label">{monthLabel(month)}</span>
        <Button
          variant="icon"
          aria-label={`Next month for ${title}`}
          onClick={() => setMonth(shiftMonth(month, 1))}
        >
          <ChevronRight size={16} strokeWidth={1.75} />
        </Button>
      </div>

      <input
        ref={fileInput}
        type="file"
        accept=".pdf,.xlsx,.csv"
        multiple
        className="visually-hidden"
        tabIndex={-1}
        onChange={addFiles}
      />

      {files.length ? (
        <div className="rows card-form">
          {files.map((file, index) => (
            <div key={`${file.name}-${index}`} className="row">
              <div className="row-main">
                <div className="row-primary wraps">{file.name}</div>
                <div className="row-meta">{formatBytes(file.size)}</div>
              </div>
              <div className="row-trail">
                <Button variant="ghost" onClick={() => dropFile(index)}>
                  Remove
                </Button>
              </div>
            </div>
          ))}
        </div>
      ) : null}

      <div className="btnrow">
        <Button onClick={() => fileInput.current?.click()} disabled={files.length >= 20}>
          Add files
        </Button>
        <span className="spacer" />
        <Button variant="primary" onClick={start} disabled={saving || files.length === 0}>
          {saving ? 'Uploading' : `Start the ${monthLabel(month)} import`}
        </Button>
      </div>
    </Panel>
  )
}

export default function ActivityImports() {
  const confirm = useConfirm()
  const [data, setData] = useState(null)
  const [loadError, setLoadError] = useState(null)
  const [rowError, setRowError] = useState(null)

  const load = useCallback(async () => {
    try {
      setData(await listActivityImports())
      setLoadError(null)
    } catch (caught) {
      setLoadError(caught.message)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  async function drop(batch) {
    const sure = await confirm(
      batch.status === 'COMMITTED'
        ? `Delete the ${monthLabel(batch.serviceMonth)} ${activitySourceLabel(batch.source).toLowerCase()} import? Every activity record it created is removed with it.`
        : `Delete the ${monthLabel(batch.serviceMonth)} ${activitySourceLabel(batch.source).toLowerCase()} import? Its files and draft lines are removed.`,
      { title: 'Delete the import', confirmLabel: 'Delete import' },
    )
    if (!sure) return
    setRowError(null)
    try {
      await removeActivityImport(batch.id)
      load()
    } catch (caught) {
      setRowError(caught.message)
    }
  }

  if (loadError) {
    return <Notice tone="bad">{loadError}</Notice>
  }
  if (!data) return <div className="center-note">Loading</div>

  return (
    <>
      <div className="toolbar">
        <ButtonLink to="/activity">Back to activity</ButtonLink>
        <span className="spacer" />
      </div>

      {data.parsingConfigured ? null : (
        <Notice tone="warn">
          Parsing is not configured. Set ANTHROPIC_ACTIVITY_API_KEY on the server to enable the
          import; files can still be attached but nothing can be extracted from them until then.
        </Notice>
      )}

      <div className="split">
        {ACTIVITY_SOURCES.map((source) => (
          <ImportSection
            key={source.value}
            source={source.value}
            title={source.label}
            description={`${source.description} Pick the service month and attach everything you have for it: PDFs, Excel workbooks, CSVs. You review every extracted line before anything lands.`}
          />
        ))}
      </div>

      {rowError ? <Notice tone="bad">{rowError}</Notice> : null}

      <Panel title="Past imports" support={`${data.imports.length} on file`}>
        {data.imports.length === 0 ? (
          <EmptyState title="Nothing imported yet">
            Each month's import appears here with what it produced.
          </EmptyState>
        ) : (
          <div className="rows">
            {data.imports.map((batch) => (
              <div key={batch.id} className="row">
                <div className="row-main">
                  <div className="row-primary">
                    <Link to={`/activity/imports/${batch.id}`}>
                      {monthLabel(batch.serviceMonth)}
                    </Link>{' '}
                    <Pill tone={activitySourceTone[batch.source]}>
                      {activitySourceLabel(batch.source)}
                    </Pill>
                  </div>
                  <div className="row-meta">
                    {[
                      `${batch.files.length} ${batch.files.length === 1 ? 'file' : 'files'}`,
                      batch.status === 'COMMITTED'
                        ? `${batch.counts.records} records`
                        : `${batch.counts.lines} draft lines`,
                      batch.createdByName,
                      formatInstant(batch.createdAt),
                    ]
                      .filter(Boolean)
                      .join(SEPARATOR)}
                  </div>
                </div>
                <div className="row-trail">
                  <Pill tone={importStatusTone[batch.status]}>
                    {importStatusLabel(batch.status)}
                  </Pill>
                  <Button variant="ghost" onClick={() => drop(batch)}>
                    Delete
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </>
  )
}
